import React, { useState, useEffect } from 'react'
import { CloseOutlined } from '@ant-design/icons'
import {
  Button,
  Card,
  Divider,
  SEO,
  Title,
  Select,
  showMessage,
  Error,
  Skeleton,
  Spin,
} from 'components/common'
import Layout from 'components/layouts/tow-column'
import {
  useCustomersQuery,
  Customer,
  useCustomerUpdateMutation,
  CustomerUpdateInput,
  CustomersDocument,
  CustomerType,
} from 'lib/graphql/storeadmin'

export const Head = () => <SEO title="アリーナ予約設定" />

type Option = {
  label: string
  value: string
}

/**
 * 顧客データをoption表示用に整形する
 */
const createCustomerOption = (customer: Customer): Option => ({
  label: `${customer.corporationName}(${customer.fullname})`,
  value: customer.id,
})

/**
 * サブアリーナ予約可能フラグがtrueかfalseかでリストを振り分ける
 */
const separateSubArenaCustomers = (data) => {
  const subArenaPermittedCustomers: Customer[] = []
  const nonSubArenaPermittedCustomers: Customer[] = []
  data.forEach(({ customer }) => {
    if (
      customer.metaFields.edges.some(
        ({ node }) => node.key === 'subarena-permitted'
      )
    ) {
      subArenaPermittedCustomers.push(customer)
    } else {
      nonSubArenaPermittedCustomers.push(customer)
    }
  })
  return {
    subArenaPermittedCustomers,
    nonSubArenaPermittedCustomers,
  }
}

/**
 * 県内団体フラグがtrueかfalseかでリストを振り分ける
 */
const separateLocalOrganizationCustomers = (data) => {
  const localOrganizationCustomers: Customer[] = []
  const nonLocalOrganizationCustomers: Customer[] = []
  data.forEach(({ customer }) => {
    if (
      customer.metaFields.edges.some(
        ({ node }) => node.key === 'local-organization'
      )
    ) {
      localOrganizationCustomers.push(customer)
    } else if (
      customer.metaFields.edges.some(
        ({ node }) => node.key === 'subarena-permitted'
      )
    ) {
      nonLocalOrganizationCustomers.push(customer)
    }
  })
  return {
    localOrganizationCustomers,
    nonLocalOrganizationCustomers,
  }
}

const ArenaSettingPage: React.FC = () => {
  const {
    data: customersData,
    loading: customersLoading,
    error: customersError,
  } = useCustomersQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      // アリーナ予約設定には「法人」の顧客データのみ使用
      type: CustomerType.Corporate,
    },
  })

  const corporateCustomers = customersData?.customers.edges

  const [updateCustomer, { loading: customerUpdateLoading }] =
    useCustomerUpdateMutation({
      awaitRefetchQueries: true,
      refetchQueries: [{ query: CustomersDocument }],
    })
  const [subArenaPermittedOptions, setSubArenaPermittedOptions] = useState<
    Option[]
  >([])
  const [nonSubArenaPermittedOptions, setNonSubArenaPermittedOptions] =
    useState<Option[]>([])
  const [localOrganizationOptions, setLocalOrganizationOptions] = useState<
    Option[]
  >([])
  const [nonLocalOrganizationOptions, setNonLocalOrganizationOptions] =
    useState<Option[]>([])
  const [selectedSubArenaPermittedValue, setSelectedSubArenaPermittedValue] =
    useState<string>('')
  const [selectedLocalOrganizationValue, setSelectedLocalOrganizationValue] =
    useState<string>('')
  const [loading, setLoading] = useState({
    subArena: false,
    localOrganization: false,
  })

  useEffect(() => {
    if (!customersData) return
    const { subArenaPermittedCustomers, nonSubArenaPermittedCustomers } =
      separateSubArenaCustomers(corporateCustomers)
    const { localOrganizationCustomers, nonLocalOrganizationCustomers } =
      separateLocalOrganizationCustomers(corporateCustomers)

    setSubArenaPermittedOptions(
      subArenaPermittedCustomers.map((customer) =>
        createCustomerOption(customer)
      )
    )
    setNonSubArenaPermittedOptions(
      nonSubArenaPermittedCustomers.map((customer) =>
        createCustomerOption(customer)
      )
    )
    setLocalOrganizationOptions(
      localOrganizationCustomers.map((customer) =>
        createCustomerOption(customer)
      )
    )
    setNonLocalOrganizationOptions(
      nonLocalOrganizationCustomers.map((customer) =>
        createCustomerOption(customer)
      )
    )

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customersData])

  if (customersLoading) return <Skeleton active />
  if (customersError) return Error.render(customersError)

  /**
   * ユーザーをlabelで検索できるようにするための関数
   */
  const handleSearch = (input, option) => {
    if (!option) return
    return option.label?.toLowerCase().indexOf(input.toLowerCase()) >= 0
  }

  // onFinishに予約を許可するリストと許可済みリストを合わせたデータを渡す
  type TargetType = 'subArena' | 'localOrganization'
  type ActionType = 'create' | 'delete'
  const handleCustomerUpdate = async (
    e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLInputElement>,
    target: TargetType,
    action: ActionType,
    id: string
  ) => {
    e.preventDefault()
    // バリデーション
    if (customerUpdateLoading) return
    if (action === 'create' && target === 'subArena' && !id) return
    if (action === 'create' && target === 'localOrganization' && !id) return

    // 更新対象のユーザーを抽出する
    const user = corporateCustomers.find(({ customer }) => customer.id === id)

    // 更新データのひな型を作成する
    const input: CustomerUpdateInput = {
      subArenaBookableFlag: user.customer.metaFields.edges.some(
        ({ node }) => node.key === 'subarena-permitted'
      ),
      prefecturalGroupFlag: user.customer.metaFields.edges.some(
        ({ node }) => node.key === 'local-organization'
      ),
    }

    // keyに応じてひな型とローディングを更新する
    if (target === 'subArena') {
      if (action === 'create') {
        setLoading({ ...loading, subArena: true })
        input.subArenaBookableFlag = true
      }
      if (action === 'delete') {
        // 県内団体フラグはサブアリーナ予約可能フラグがtrueのときに付与できるフラグであるため、
        // サブアリーナ予約可能フラグをはく奪する場合は県内団体フラグもはく奪する
        setLoading({ subArena: true, localOrganization: true })
        input.subArenaBookableFlag = false
        input.prefecturalGroupFlag = false
      }
    }
    if (target === 'localOrganization') {
      setLoading({ ...loading, localOrganization: true })
      input.prefecturalGroupFlag = action === 'create' ? true : false
    }

    try {
      const { data } = await updateCustomer({
        variables: { allowDuplicate: true, id, input },
      })
      if (data) {
        const targetMessage =
          target === 'subArena' ? 'サブアリーナ予約可能' : '県内団体'
        const actionMessage = action === 'create' ? 'に追加' : 'から削除'
        showMessage('success', `${targetMessage}リスト${actionMessage}しました`)
      }
    } catch (e) {
      console.log(e)
      showMessage('error', 'データの更新に失敗しました')
    }

    setSelectedLocalOrganizationValue('')
    setSelectedSubArenaPermittedValue('')
    setLoading({ subArena: false, localOrganization: false })
  }

  return (
    <Layout title="SAGA アリーナ予約設定">
      {/* サブアリーナ予約可能フラグ */}
      <Layout.Section size="large">
        <Title level={4}>サブアリーナ予約可能フラグの設定</Title>
        <p className="opacity-90">
          サブアリーナを予約可能なお客様を設定できます。
          <br />
          設定されたお客様はユーザー利用者ページからサブアリーナを予約することができるようになります。
        </p>
      </Layout.Section>
      <Layout.Section size="large">
        <Card shadow>
          <form
            onSubmit={(e: React.FormEvent<HTMLFormElement>) =>
              handleCustomerUpdate(
                e,
                'subArena',
                'create',
                selectedSubArenaPermittedValue
              )
            }
            className="mb-8"
          >
            <h3>サブアリーナの予約を許可するお客様</h3>
            <div className="grid grid-cols-3 gap-x-4">
              <Select
                className="col-span-2"
                options={nonSubArenaPermittedOptions}
                placeholder="クリックまたはキー入力で検索"
                style={{ width: '100%' }}
                filterOption={handleSearch}
                listHeight={192}
                showSearch
                onChange={setSelectedSubArenaPermittedValue}
                value={selectedSubArenaPermittedValue}
              />
              <Button type="primary" htmlType="submit">
                予約可能リストに追加
              </Button>
            </div>
          </form>
          <h3>サブアリーナ予約可能リスト</h3>
          <Spin
            tip="Loading"
            size="large"
            spinning={loading.subArena && customerUpdateLoading}
          >
            {subArenaPermittedOptions.length <= 0 ? (
              <div className="text-center py-16 opacity-80">
                まだサブアリーナ予約可能リストはありません
              </div>
            ) : (
              <ul className="grid grid-cols-2 gap-4">
                {subArenaPermittedOptions.map((option: Option) => (
                  <li
                    key={option.value}
                    className="px-4 py-2 drop-shadow-sm border flex justify-between items-center bg-zinc-50 rounded"
                  >
                    <span>{option.label}</span>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<CloseOutlined />}
                      size="small"
                      onClick={(e: React.MouseEvent<HTMLInputElement>) =>
                        handleCustomerUpdate(
                          e,
                          'subArena',
                          'delete',
                          option.value
                        )
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </Spin>
        </Card>
      </Layout.Section>

      <Divider space="large" />

      {/* 県内団体フラグ */}
      <Layout.Section size="large">
        <Title level={4}>県内団体フラグの設定</Title>
        <p className="opacity-90">
          県内団体のお客様を設定できます。
          <br />
          設定されたお客様は通常とは異なる期間でSAGAアリーナ内の施設を予約することができるようになります。
        </p>
        <p className="opacity-90">
          ※県内団体リストに追加するには、そのお客様がサブアリーナ予約可能リストに含まれている必要があります。
        </p>
      </Layout.Section>

      <Layout.Section size="large">
        <Card shadow>
          <form
            onSubmit={(e: React.FormEvent<HTMLFormElement>) =>
              handleCustomerUpdate(
                e,
                'localOrganization',
                'create',
                selectedLocalOrganizationValue
              )
            }
            className="mb-8"
          >
            <h3>県内団体のお客様を選択する</h3>
            <div className="grid grid-cols-3 gap-x-4">
              <div className="col-span-2">
                <Select
                  options={nonLocalOrganizationOptions}
                  placeholder="クリックまたはキー入力で検索"
                  style={{ width: '100%' }}
                  filterOption={handleSearch}
                  listHeight={192}
                  showSearch
                  onChange={setSelectedLocalOrganizationValue}
                  value={selectedLocalOrganizationValue}
                />
              </div>
              <Button type="primary" htmlType="submit">
                県内団体リストに追加
              </Button>
            </div>
          </form>
          <h3>県内団体リスト</h3>
          <Spin
            tip="Loading"
            size="large"
            spinning={loading.localOrganization && customerUpdateLoading}
          >
            {localOrganizationOptions.length <= 0 ? (
              <div className="text-center py-16 opacity-80">
                まだ県内団体リストはありません
              </div>
            ) : (
              <ul className="grid grid-cols-2 gap-4">
                {localOrganizationOptions.map((option: Option) => (
                  <li
                    key={option.value}
                    className="px-4 py-2 drop-shadow-sm border flex justify-between items-center bg-zinc-50 rounded"
                  >
                    <span>{option.label}</span>
                    <Button
                      type="text"
                      shape="circle"
                      icon={<CloseOutlined />}
                      size="small"
                      onClick={(e: React.MouseEvent<HTMLInputElement>) =>
                        handleCustomerUpdate(
                          e,
                          'localOrganization',
                          'delete',
                          option.value
                        )
                      }
                    />
                  </li>
                ))}
              </ul>
            )}
          </Spin>
        </Card>
      </Layout.Section>
    </Layout>
  )
}

export default ArenaSettingPage
